// API service for backend integration
// This handles REST API calls to the FastAPI backend

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform-api-dev.ngpes.com/dasp/pre-onboarding-backend/api';

// Auth0 token management
let getAccessTokenSilently = null;

export const setAuth0TokenGetter = (tokenGetter) => {
  console.log('[API] setAuth0TokenGetter called with:', !!tokenGetter);
  getAccessTokenSilently = tokenGetter;
};

// Debug function to check if token getter is available
export const isTokenGetterAvailable = () => {
  return !!getAccessTokenSilently;
};

// Get authorization headers
const getAuthHeaders = async () => {
  console.log('[API] Getting auth headers...');

  const headers = {
    'Content-Type': 'application/json',
  };

  if (getAccessTokenSilently) {
    try {
      console.log('[API] Token getter available, requesting token...');
      const token = await getAccessTokenSilently();

      if (token) {
        console.log(`[API] ✅ Token obtained successfully (length: ${token.length})`);
        console.log(`[API] Token preview: ${token.substring(0, 30)}...`);
        headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('[API] ❌ Token getter returned null/undefined');
      }
    } catch (error) {
      console.error('[API] ❌ Failed to get access token:', error);
    }
  } else {
    console.warn('[API] ❌ No token getter available');
  }

  console.log('[API] Final headers:', {
    ...headers,
    Authorization: headers.Authorization ? `Bearer ${headers.Authorization.substring(7, 37)}...` : 'Not set'
  });

  return headers;
};

class ApiService {
  // Save section data as draft
  async saveSectionDraft(sectionId, data) {
    console.log(`[API] Saving draft for section: ${sectionId}`, data);

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/applications/sections`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          section_id: sectionId,
          data: data
        })
      });

      const result = await response.json();

      if (!response.ok) {
        console.error(`[API] Section save failed:`, result);
        return {
          success: false,
          message: result.detail || 'Failed to save section data',
          errors: result.errors || {}
        };
      }

      console.log(`[API] Section ${sectionId} saved successfully:`, result);
      return {
        success: true,
        message: result.message || 'Section data saved successfully',
        data: {
          sectionId: result.section_id,
          savedAt: result.saved_at,
          status: result.status
        }
      };

    } catch (error) {
      console.error(`[API] Network error saving section ${sectionId}:`, error);
      return {
        success: false,
        message: 'Network error occurred while saving section data',
        errors: {}
      };
    }
  }

  // Get saved section data
  async getSectionData(sectionId) {
    console.log(`[API] Getting data for section: ${sectionId}`);

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/applications/sections/${sectionId}`, {
        method: 'GET',
        headers
      });

      const result = await response.json();

      if (!response.ok) {
        console.error(`[API] Section data retrieval failed:`, result);
        return {
          success: false,
          message: result.detail || 'Failed to retrieve section data',
          data: {
            sectionId,
            data: {},
            status: 'not_started',
            lastModified: null
          }
        };
      }

      console.log(`[API] Section ${sectionId} data retrieved successfully:`, result);
      return {
        success: true,
        message: 'Section data retrieved successfully',
        data: {
          sectionId: result.section_id,
          data: result.data,
          status: result.status,
          lastModified: result.last_modified
        }
      };

    } catch (error) {
      console.error(`[API] Network error retrieving section ${sectionId}:`, error);
      return {
        success: false,
        message: 'Network error occurred while retrieving section data',
        data: {
          sectionId,
          data: {},
          status: 'not_started',
          lastModified: null
        }
      };
    }
  }

  // Submit complete form using saved section data
  async submitForm(formData) {
    console.log('[API] Submitting application using saved section data');

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/applications/submit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          confirm_submission: true
        })
      });

      const result = await response.json();

      if (!response.ok) {
        console.error(`[API] Application submission failed:`, result);
        return {
          success: false,
          message: result.detail || 'Failed to submit application',
          errors: result.errors || {}
        };
      }

      console.log(`[API] Application submitted successfully:`, result);
      return {
        success: true,
        message: 'Application submitted successfully',
        data: {
          submissionId: result.user_id,
          submittedAt: new Date().toISOString(),
          status: 'submitted',
          referenceNumber: result.reference_number,
          freshdeskTicketId: result.freshdesk_ticket_id,
          freshdeskTicketUrl: result.freshdesk_ticket_url
        }
      };

    } catch (error) {
      console.error(`[API] Network error submitting application:`, error);
      return {
        success: false,
        message: 'Network error occurred while submitting application',
        errors: {}
      };
    }
  }

  // Upload file
  async uploadFile(file, fieldName) {
    console.log(`[API] Uploading file: ${file.name} for field: ${fieldName}`);

    try {
      // Get auth headers (without Content-Type for FormData)
      const authHeaders = await getAuthHeaders();
      const headers = {};
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', fieldName); // Add document type parameter

      const response = await fetch(`${API_BASE_URL}/applications/files`, {
        method: 'POST',
        headers,
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        console.error(`[API] File upload failed:`, result);
        return {
          success: false,
          message: result.detail || 'Failed to upload file'
        };
      }

      console.log(`[API] File uploaded successfully:`, result);
      return {
        success: true,
        message: 'File uploaded successfully',
        data: {
          fileId: result.file_id,
          fileName: result.file_name,
          fileSize: result.file_size,
          fileType: file.type,
          uploadedAt: new Date().toISOString(),
          url: `${API_BASE_URL}/files/${result.file_id}`
        }
      };

    } catch (error) {
      console.error(`[API] Network error uploading file:`, error);
      return {
        success: false,
        message: 'Network error occurred while uploading file'
      };
    }
  }

  // Get application status
  async getApplicationStatus() {
    console.log('[API] Getting application status');

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/applications/status`, {
        method: 'GET',
        headers
      });

      const result = await response.json();

      if (!response.ok) {
        console.error(`[API] Application status retrieval failed:`, result);
        return {
          success: false,
          message: result.detail || 'Failed to retrieve application status',
          data: {
            hasApplication: false,
            status: null,
            referenceNumber: null,
            submissionDate: null,
            lastUpdated: null,
            isReadOnly: false
          }
        };
      }

      console.log(`[API] Application status retrieved successfully:`, result);
      return {
        success: true,
        message: 'Application status retrieved successfully',
        data: {
          hasApplication: result.has_application,
          status: result.status,
          referenceNumber: result.reference_number,
          submissionDate: result.submission_date,
          lastUpdated: result.last_updated,
          isReadOnly: result.status === 'submitted'
        }
      };

    } catch (error) {
      console.error(`[API] Network error retrieving application status:`, error);
      return {
        success: false,
        message: 'Network error occurred while retrieving application status',
        data: {
          hasApplication: false,
          status: null,
          referenceNumber: null,
          submissionDate: null,
          lastUpdated: null,
          isReadOnly: false
        }
      };
    }
  }

  // Load all sections data for existing application
  async loadAllSectionsData() {
    console.log('[API] Loading all sections data');

    const sectionIds = [
      'companyDetails',
      'contactInformation',
      'pepInformation',
      'sanctionsAudit',
      'businessPresentation',
      'documentation'
    ];

    const sectionsData = {};
    let hasAnyData = false;

    for (const sectionId of sectionIds) {
      try {
        const result = await this.getSectionData(sectionId);
        if (result.success && result.data.data && Object.keys(result.data.data).length > 0) {
          sectionsData[sectionId] = {
            data: result.data.data,
            status: 'completed', // Mark as completed if data exists
            errors: {}
          };
          hasAnyData = true;
        } else {
          sectionsData[sectionId] = {
            data: {},
            status: 'not_started',
            errors: {}
          };
        }
      } catch (error) {
        console.error(`[API] Error loading section ${sectionId}:`, error);
        sectionsData[sectionId] = {
          data: {},
          status: 'not_started',
          errors: {}
        };
      }
    }

    console.log('[API] Loaded sections data:', sectionsData);
    return {
      success: true,
      message: 'Sections data loaded successfully',
      data: {
        sections: sectionsData,
        hasData: hasAnyData
      }
    };
  }

  // Get form status (legacy method for backward compatibility)
  async getFormStatus() {
    console.log('[API] Getting form status (legacy)');
    const statusResult = await this.getApplicationStatus();

    if (!statusResult.success) {
      return {
        success: false,
        message: statusResult.message,
        data: {
          isSubmitted: false,
          submissionId: null,
          lastModified: new Date().toISOString(),
          completedSections: [],
          totalSections: 6
        }
      };
    }

    return {
      success: true,
      message: 'Form status retrieved successfully',
      data: {
        isSubmitted: statusResult.data.status === 'submitted',
        submissionId: statusResult.data.referenceNumber,
        lastModified: statusResult.data.lastUpdated,
        completedSections: [],
        totalSections: 6,
        status: statusResult.data.status,
        referenceNumber: statusResult.data.referenceNumber
      }
    };
  }
}

export default new ApiService();
